import { AuthorizationServiceConfiguration, FetchRequestor, AuthorizationRequest, BaseTokenRequestHandler, TokenRequest, GRANT_TYPE_AUTHORIZATION_CODE, GRANT_TYPE_REFRESH_TOKEN, } from '@openid/appauth';
import { invariant } from '@streem/toolbox';
import { RedirectRequestHandler } from '../oauth/redirect_based_handler';
import { OAuthError } from '../errors';
import { logger } from '../utils/logger';
// This local storage key is also referenced in the script src linked in logout-success.html
// If you change the string value you must also change it in that static file
export const REFRESH_TOKEN_STORAGE_KEY = 'FUSIONAUTH_REFRESH_TOKEN';
export const REDIRECT_URI = typeof window !== 'undefined' ? window.location.origin + '/redirect.html' : '';
export class OAuthManager {
    constructor() {
        this.requestor = new FetchRequestor();
        this.handler = new RedirectRequestHandler();
        this.tokenHandler = new BaseTokenRequestHandler(this.requestor);
    }
    async performAuthorizationRequest(config, offlineAccess = false) {
        invariant(
        // eslint-disable-next-line camelcase
        config.login_configuration?.open_id?.client_id, 'Company is not configured for OAuth');
        const authServiceConfig = await this.getAuthServiceConfig(config);
        const request = new AuthorizationRequest({
            client_id: config.login_configuration.open_id.client_id,
            redirect_uri: REDIRECT_URI,
            scope: offlineAccess ? 'openid email offline_access' : 'openid email',
            response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
            extras: {
                idp_hint: config.login_configuration.open_id.idp_hint || '',
                forgot_password_override: window.location.origin + '/forgot',
            },
        });
        await this.handler.performAuthorizationRequest(authServiceConfig, request);
    }
    pendingAuthorizationRequest() {
        return this.handler.pendingAuthorizationRequest();
    }
    async completeAuthorizationRequest() {
        const authRequestResponse = this.handler.completeAuthorizationRequestSync();
        if (authRequestResponse?.response) {
            const { request, response, serviceConfiguration } = authRequestResponse;
            const tokenResponse = await this.requestAccessToken(serviceConfiguration, request, response);
            invariant(tokenResponse.idToken);
            if (tokenResponse.refreshToken) {
                this.storeRefreshTokenConfig(request.clientId, tokenResponse.refreshToken, serviceConfiguration);
            }
            return tokenResponse;
        }
        else if (authRequestResponse?.error) {
            throw new OAuthError(authRequestResponse.error.errorDescription || authRequestResponse.error.error);
        }
        return;
    }
    refreshTokenAvailable() {
        const config = this.getRefreshTokenConfig();
        return config !== undefined;
    }
    async authorizeWithRefreshToken() {
        const refreshConfig = this.getRefreshTokenConfig();
        if (!refreshConfig) {
            return;
        }
        const tokenRequest = new TokenRequest({
            client_id: refreshConfig.clientId,
            redirect_uri: REDIRECT_URI,
            grant_type: GRANT_TYPE_REFRESH_TOKEN,
            refresh_token: refreshConfig.token,
        });
        const response = await this.tokenHandler.performTokenRequest(refreshConfig.authorizationServiceConfiguration, tokenRequest);
        invariant(response.idToken);
        if (response.refreshToken && response.refreshToken !== refreshConfig.token) {
            this.storeRefreshTokenConfig(refreshConfig.clientId, response.refreshToken, refreshConfig.authorizationServiceConfiguration);
        }
        return response;
    }
    clearStorage() {
        window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }
    async requestAccessToken(config, request, response) {
        const tokenRequest = new TokenRequest({
            client_id: request.clientId,
            redirect_uri: request.redirectUri,
            grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
            code: response.code,
            extras: {
                // eslint-disable-next-line camelcase
                code_verifier: request.internal?.code_verifier ?? '',
            },
        });
        return this.tokenHandler.performTokenRequest(config, tokenRequest);
    }
    async getAuthServiceConfig(config) {
        invariant(
        // eslint-disable-next-line camelcase
        config.login_configuration?.open_id?.authorization_endpoint, 'Company is not configured for OAuth');
        const authorizationEndpoint = new URL(config.login_configuration.open_id.authorization_endpoint);
        return AuthorizationServiceConfiguration.fetchFromIssuer(authorizationEndpoint.origin, this.requestor);
    }
    storeRefreshTokenConfig(clientId, token, authorizationServiceConfiguration) {
        const refreshConfig = JSON.stringify({
            token,
            clientId,
            authorizationServiceConfiguration,
        });
        window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshConfig);
    }
    getRefreshTokenConfig() {
        const json = window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
        if (!json || json.length < 0) {
            return;
        }
        try {
            const config = JSON.parse(json);
            invariant(config.token && config.clientId && config.authorizationServiceConfiguration);
            return config;
        }
        catch (e) {
            logger.error('Unable to parse refresh token config.', e);
            window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
            return;
        }
    }
}
//# sourceMappingURL=oauth_manager.js.map